<template>
  <div class="enrollment-import pa-10">
    <title-page>
      {{ $t('onboarding.import-csv') }}
    </title-page>

    <v-card class="pa-6">
      <h3 class="text-h6 mb-4">{{ $t('onboarding.import-csv-description') }}</h3>

      <!-- Download template -->
      <v-alert type="info" outlined dense class="mb-4">
        <div>{{ $t('onboarding.csv-template-info') }}</div>
        <v-btn small text color="primary" @click="downloadTemplate" class="mt-2">
          <v-icon left small>mdi-download</v-icon>
          {{ $t('onboarding.download-template') }}
        </v-btn>
      </v-alert>

      <!-- File upload -->
      <v-file-input
        v-model="file"
        :label="$t('onboarding.select-csv-file')"
        accept=".csv"
        outlined
        dense
        prepend-icon="mdi-file-delimited"
        :disabled="loading"
        @change="handleFileChange"
      ></v-file-input>

      <!-- Preview -->
      <div v-if="previewResult" class="mt-4">
        <v-alert type="success" outlined dense class="mb-2">
          {{ $t('onboarding.import-preview') }}
        </v-alert>

        <v-simple-table dense class="mb-4">
          <tbody>
            <tr v-if="previewResult.created !== undefined">
              <td><strong>{{ $t('onboarding.created') }}:</strong></td>
              <td class="success--text">{{ previewResult.created || 0 }}</td>
            </tr>
            <tr v-if="previewResult.sent !== undefined">
              <td><strong>{{ $t('onboarding.sent') }}:</strong></td>
              <td class="success--text">{{ previewResult.sent || 0 }}</td>
            </tr>
            <tr v-if="previewResult.skipped !== undefined">
              <td><strong>{{ $t('onboarding.skipped') }}:</strong></td>
              <td class="info--text">{{ previewResult.skipped || 0 }}</td>
            </tr>
            <tr v-if="previewResult.duplicates !== undefined">
              <td><strong>{{ $t('onboarding.duplicates') }}:</strong></td>
              <td class="warning--text">{{ previewResult.duplicates || 0 }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('onboarding.errors') }}:</strong></td>
              <td class="error--text">{{ previewResult.errors || 0 }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <!-- Error details -->
        <div v-if="previewResult.details && previewResult.details.errors && previewResult.details.errors.length > 0" class="mb-4">
          <v-alert type="error" outlined dense>
            <div class="text-subtitle-2 mb-2">{{ $t('onboarding.error-details') }}</div>
            <div v-for="(errorItem, idx) in previewResult.details.errors" :key="idx" class="mb-2">
              <div class="text-caption">
                <!-- Import errors have 'row', confirm errors have 'enrollmentId' -->
                <strong v-if="errorItem.row">{{ $t('onboarding.row') }} {{ errorItem.row }}:</strong>
                <strong v-else-if="errorItem.enrollmentId">{{ $t('email') }}:</strong>
                {{ errorItem.error }}
              </div>
              <!-- Import errors have 'data.email', confirm errors have 'email' directly -->
              <div v-if="errorItem.data?.email || errorItem.email" class="text-caption text--secondary ml-4">
                {{ $t('email') }}: {{ errorItem.data?.email || errorItem.email }}
              </div>
            </div>
          </v-alert>
        </div>

        <!-- Duplicate details -->
        <div v-if="previewResult.details && previewResult.details.duplicates && previewResult.details.duplicates.length > 0" class="mb-4">
          <v-alert type="warning" outlined dense>
            <div class="text-subtitle-2 mb-2">{{ $t('onboarding.duplicate-details') }}</div>
            <div v-for="(dupItem, idx) in previewResult.details.duplicates" :key="idx" class="text-caption">
              {{ $t('onboarding.row') }} {{ dupItem.row }}: {{ dupItem.email }}
            </div>
          </v-alert>
        </div>

        <!-- Confirm button -->
        <v-btn
          color="primary"
          small
          :loading="confirmLoading"
          :disabled="!previewResult.batchId || previewResult.created === 0"
          @click="confirmImport"
        >
          <v-icon left small>mdi-check</v-icon>
          {{ $t('onboarding.confirm-and-send-emails', { count: previewResult.created || 0 }) }}
        </v-btn>
      </div>

      <v-divider class="my-4"></v-divider>

      <div class="d-flex justify-start">
        <v-btn text small @click="$router.back()">
          <v-icon left small>mdi-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
import PaymentOnboardingRepository from '@repository/PaymentOnboardingRepository'

export default {
  name: 'EnrollmentImport',

  mixins: [],

  data() {
    return {
      file: null,
      loading: false,
      confirmLoading: false,
      previewResult: null
    }
  },

  methods: {
    async downloadTemplate() {
      try {
        const csvContent = await PaymentOnboardingRepository.getEnrollmentTemplate()

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        link.setAttribute('href', url)
        link.setAttribute('download', 'enrollment_template.csv')
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (err) {
        this.notifyError(err)
      }
    },

    async handleFileChange() {
      if (!this.file) {
        this.previewResult = null
        return
      }

      this.loading = true
      this.previewResult = null

      try {
        const data = await PaymentOnboardingRepository.importCsv(this.file)
        this.previewResult = data
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    async confirmImport() {
      if (!this.previewResult?.batchId) return

      this.confirmLoading = true

      try {
        const result = await PaymentOnboardingRepository.confirmBatch(this.previewResult.batchId)

        // Check if there are errors in the result
        if (result.errors > 0 || (result.details?.errors && result.details.errors.length > 0)) {
          // Update preview result with confirmation result to show errors
          this.previewResult = {
            ...this.previewResult,
            sent: result.sent,
            skipped: result.skipped,
            errors: result.errors,
            details: result.details
          }

          // Show error notification
          this.$dialog?.notify?.error && this.$dialog.notify.error(
            this.$t('onboarding.confirm-batch-errors', { errors: result.errors })
          )
        } else {
          // Success - redirect to list
          this.$dialog?.notify?.success && this.$dialog.notify.success(
            this.$t('onboarding.import-success', { count: result.sent || this.previewResult.created })
          )
          this.$router.push({ name: 'payment-onboarding.enrollments' })
        }
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.confirmLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.enrollment-import {}
</style>
